'use client'

import { reportHandledException } from '@/lib/error-reporting'
import { useEffect, useRef } from 'react'

export interface RefreshOnRefocusOptions {
  /** Subscribe only while the surface that needs fresh data is on screen. */
  enabled: boolean
  /** Runs once per return to the page. Rejections are swallowed by design. */
  onRefocus: () => Promise<void>
}

/**
 * Re-runs a loader when the user comes BACK to this page — from another tab,
 * another window, or another app.
 *
 * A long-lived surface that fetched once on mount goes stale as soon as the
 * data is edited somewhere else, and the app links out to those editors itself
 * (`target="_blank"`). Returning to the page is the moment to catch up.
 *
 * Returning fires up to two events (`visibilitychange` then `focus`), so the
 * trigger is armed by the LEAVING event instead: `blur` / hidden marks the page
 * dirty, and the first arrival event consumes that mark. Listeners are only
 * attached while `enabled`, so a departure that happened while the surface was
 * closed never arms a refresh.
 */
export function useRefreshOnRefocus({ enabled, onRefocus }: RefreshOnRefocusOptions): void {
  const onRefocusRef = useRef(onRefocus)
  onRefocusRef.current = onRefocus
  // Held across effect rebuilds on purpose: `enabled` flips whenever the caller
  // starts and abandons a submit, and a per-closure flag would let the new
  // subscription start a second run alongside one still in flight. The arming
  // mark and the trailing-run entry point travel with it — split between a ref
  // and a closure they disagree, and a return that the shared flag turned away
  // would be forgotten by the run that was supposed to pick it up.
  const runningRef = useRef(false)
  const leftThePageRef = useRef(false)
  const returnedRef = useRef<() => void>(() => {})

  useEffect(() => {
    if (!enabled) return

    let disposed = false

    const markLeft = () => {
      leftThePageRef.current = true
    }

    const returned = () => {
      // A run already in flight is fetching pre-edit data, so an edit made
      // during it would stay invisible. The mark is deliberately LEFT armed and
      // consumed by a trailing run when the user is back — firing that run
      // while they are still away would burn the mark on data they are still
      // editing, and the real return would then find nothing to do.
      if (disposed || !leftThePageRef.current || runningRef.current) return
      leftThePageRef.current = false
      runningRef.current = true
      void onRefocusRef
        .current()
        .catch((error: unknown) => {
          reportHandledException(error, { source: 'hooks/use-refresh-on-refocus.ts' })
          // The caller owns user-facing failure handling; a background catch-up
          // must never surface as an unhandled rejection.
          console.error('[use-refresh-on-refocus] refresh failed', error)
        })
        .finally(() => {
          runningRef.current = false
          // Through the ref, so the CURRENT subscription picks this up: the one
          // that started the run may have been torn down meanwhile.
          if (leftThePageRef.current && isBack()) returnedRef.current()
        })
    }

    returnedRef.current = returned

    const isBack = () => document.visibilityState === 'visible' && document.hasFocus()

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') returned()
      else markLeft()
    }
    const handleBlur = () => markLeft()

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('blur', handleBlur)
    window.addEventListener('focus', returned)
    return () => {
      // A run in flight still has a `.finally` that would start the trailing
      // refresh; once this subscription is gone, `enabled` is false and this
      // closure may not start anything more. A re-enabled effect is held off
      // separately by `runningRef`, which outlives the rebuild.
      disposed = true
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('blur', handleBlur)
      window.removeEventListener('focus', returned)
    }
  }, [enabled])
}
